import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from 'src/store/auth';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';

import { type W2DoneReason } from '../components/W2Done';
import { W2DoneScreen } from './W2DoneScreen';
import { useW2FlowContext } from './W2FlowLayout';
import { useW2Paths } from './paths';

/**
 * First name for the waiver terminal screen (spec 3, "Thank You, {Firstname}!").
 * A waiver is submitted before the About You applicant card is filled, so the
 * account (populated at register/login) is the reliable source; the onboarding
 * primary applicant is a fallback. Read imperatively — this only runs on the
 * terminal render.
 */
function resolveFirstName(): string | undefined {
  const fromAccount = useAuthStore.getState().user?.firstName?.trim();
  if (fromAccount) return fromAccount;
  const applicants = useOnboardingStore.getState().data?.applicants;
  const primary =
    applicants?.find((a) => a.applicant === 'primary') ?? applicants?.[0];
  const fromOnboarding = primary?.firstName?.trim();
  return fromOnboarding || undefined;
}

export const W2DoneRoute: React.FC = () => {
  const { runEnroll } = useW2FlowContext();
  const location = useLocation();
  const paths = useW2Paths();
  const reason = (location.state as { reason?: W2DoneReason } | null)?.reason;
  const [hasJwt] = useState(() => Boolean(localStorage.getItem('token')));

  // Reason-less arrival WITH a JWT (in-session history nav, or a future
  // persistent-JWT world): re-run the enroll gate — terminal → back here with
  // 'returning', non-terminal → phq (C-1 done row). The layout renders its
  // loading shell while the gate runs.
  useEffect(() => {
    if (!reason && hasJwt) void runEnroll();
  }, [reason, hasJwt, runEnroll]);

  if (reason)
    return <W2DoneScreen reason={reason} firstName={resolveFirstName()} />;
  if (!hasJwt) return <Navigate to={paths.login} replace />;
  return null; // enroll gate running — layout is showing the loading shell
};
